/**
 * Rich Text Editor Component
 *
 * A simple HTML content editor for admin users to edit dynamic content sections.
 * Supports basic formatting, HTML source editing, and preview modes.
 *
 * @example
 * ```tsx
 * <RichTextEditor
 *   value={content}
 *   onChange={setContent}
 *   placeholder="Enter HTML content..."
 * />
 * ```
 */

import { useState, useRef, useCallback, type KeyboardEvent } from 'react';
import { pagesApi } from '@/services/api';

/**
 * Props for the RichTextEditor component
 */
interface RichTextEditorProps {
  /** Current content value */
  value: string;
  /** Callback when content changes */
  onChange: (value: string) => void;
  /** Placeholder text when editor is empty */
  placeholder?: string;
  /** Whether the editor is disabled */
  disabled?: boolean;
  /** Minimum height in pixels */
  minHeight?: number;
  /** Editor label */
  label?: string;
  /** Error message to display */
  error?: string;
  /**
   * Optional page id — when present, the toolbar surfaces an "Insert image"
   * action that uploads to `POST /api/pages/:id/upload` and inserts an
   * `<img>` at the cursor. Page-scoped (not part-scoped) because the
   * atomic parts-replace recreates part IDs on save.
   */
  pageId?: string;
}

/**
 * Toolbar button configuration
 */
interface ToolbarButton {
  icon: React.ReactNode;
  label: string;
  action: () => void;
  active?: boolean;
}

/**
 * View mode for the editor
 */
type ViewMode = 'visual' | 'source' | 'preview';

/**
 * Rich Text Editor with visual editing, source code, and preview modes
 */
export function RichTextEditor({
  value,
  onChange,
  placeholder = 'Enter content...',
  disabled = false,
  minHeight = 300,
  label,
  error,
  pageId,
}: RichTextEditorProps) {
  const [viewMode, setViewMode] = useState<ViewMode>('visual');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const editorRef = useRef<HTMLDivElement>(null);
  const [isSelectionBold, setIsSelectionBold] = useState(false);
  const [isSelectionItalic, setIsSelectionItalic] = useState(false);
  const [isSelectionUnderline, setIsSelectionUnderline] = useState(false);

  /**
   * Execute a document command (for contentEditable)
   */
  const execCommand = useCallback((command: string, value?: string) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
    updateToolbarState();
  }, []);

  /**
   * Update toolbar button states based on current selection
   */
  const updateToolbarState = useCallback(() => {
    setIsSelectionBold(document.queryCommandState('bold'));
    setIsSelectionItalic(document.queryCommandState('italic'));
    setIsSelectionUnderline(document.queryCommandState('underline'));
  }, []);

  /**
   * Handle content changes from contentEditable div
   */
  const handleContentChange = useCallback(() => {
    if (editorRef.current) {
      const html = editorRef.current.innerHTML;
      // Remove placeholder span if present
      const cleanHtml = html.replace(/<span class="placeholder">.*?<\/span>/g, '');
      onChange(cleanHtml === '<br>' ? '' : cleanHtml);
    }
  }, [onChange]);

  /**
   * Handle source code textarea changes
   */
  const handleSourceChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      onChange(e.target.value);
    },
    [onChange]
  );

  /**
   * Handle keyboard shortcuts
   */
  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (e.ctrlKey || e.metaKey) {
        switch (e.key.toLowerCase()) {
          case 'b':
            e.preventDefault();
            execCommand('bold');
            break;
          case 'i':
            e.preventDefault();
            execCommand('italic');
            break;
          case 'u':
            e.preventDefault();
            execCommand('underline');
            break;
        }
      }
    },
    [execCommand]
  );

  /**
   * Handle selection changes to update toolbar state
   */
  const handleSelect = useCallback(() => {
    updateToolbarState();
  }, [updateToolbarState]);

  /**
   * Insert a link
   */
  const insertLink = useCallback(() => {
    const url = prompt('Enter URL:');
    if (url) {
      execCommand('createLink', url);
    }
  }, [execCommand]);

  /**
   * Open the hidden file picker so the user can choose an image to upload.
   */
  const openImagePicker = useCallback(() => {
    if (!pageId || disabled) return;
    fileInputRef.current?.click();
  }, [pageId, disabled]);

  /**
   * Handle the chosen image: upload to the page-scoped endpoint and insert
   * an <img src="..."> at the current selection.
   */
  const handleImageSelected = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      // Reset so picking the same file twice still fires onChange.
      if (fileInputRef.current) fileInputRef.current.value = '';
      if (!file || !pageId) return;
      setUploadError(null);
      setUploading(true);
      try {
        const { url } = await pagesApi.uploadPageImage(pageId, file);
        editorRef.current?.focus();
        execCommand('insertImage', url);
      } catch (err) {
        setUploadError(err instanceof Error ? err.message : 'Upload failed');
      } finally {
        setUploading(false);
      }
    },
    [pageId, execCommand],
  );

  /**
   * Insert heading
   */
  const insertHeading = useCallback(
    (level: number) => {
      execCommand('formatBlock', `h${level}`);
    },
    [execCommand]
  );

  /**
   * Toolbar buttons configuration
   */
  const toolbarButtons: ToolbarButton[] = [
    {
      icon: <span className="font-bold">B</span>,
      label: 'Bold (Ctrl+B)',
      action: () => execCommand('bold'),
      active: isSelectionBold,
    },
    {
      icon: <span className="italic">I</span>,
      label: 'Italic (Ctrl+I)',
      action: () => execCommand('italic'),
      active: isSelectionItalic,
    },
    {
      icon: <span className="underline">U</span>,
      label: 'Underline (Ctrl+U)',
      action: () => execCommand('underline'),
      active: isSelectionUnderline,
    },
    {
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
        </svg>
      ),
      label: 'Insert Link',
      action: insertLink,
    },
    ...(pageId
      ? [
          {
            icon: (
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            ),
            label: uploading ? 'Uploading…' : 'Insert image',
            action: openImagePicker,
          },
        ]
      : []),
    {
      icon: <span className="text-xs font-bold">H1</span>,
      label: 'Heading 1',
      action: () => insertHeading(1),
    },
    {
      icon: <span className="text-xs font-bold">H2</span>,
      label: 'Heading 2',
      action: () => insertHeading(2),
    },
    {
      icon: <span className="text-xs font-bold">H3</span>,
      label: 'Heading 3',
      action: () => insertHeading(3),
    },
    {
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
        </svg>
      ),
      label: 'Unordered List',
      action: () => execCommand('insertUnorderedList'),
    },
    {
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h10" />
        </svg>
      ),
      label: 'Ordered List',
      action: () => execCommand('insertOrderedList'),
    },
    {
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
        </svg>
      ),
      label: 'Horizontal Rule',
      action: () => execCommand('insertHorizontalRule'),
    },
  ];

  return (
    <div className="space-y-2">
      {/* Label */}
      {label && (
        <label className="block text-sm font-medium text-white/80">{label}</label>
      )}

      {/* Editor Container */}
      <div
        className={`rounded-lg border overflow-hidden transition-colors ${
          error
            ? 'border-red-500/50'
            : disabled
            ? 'border-white/5 bg-white/5'
            : 'border-white/10 focus-within:border-primary/50'
        }`}
      >
        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-1 p-2 bg-white/5 border-b border-white/10">
          {/* View Mode Tabs */}
          <div className="flex items-center gap-1 mr-2 pr-2 border-r border-white/10">
            <button
              type="button"
              onClick={() => setViewMode('visual')}
              disabled={disabled}
              className={`px-2 py-1 text-xs rounded transition-colors ${
                viewMode === 'visual'
                  ? 'bg-primary text-white'
                  : 'text-white/60 hover:text-white hover:bg-white/10'
              }`}
            >
              Visual
            </button>
            <button
              type="button"
              onClick={() => setViewMode('source')}
              disabled={disabled}
              className={`px-2 py-1 text-xs rounded transition-colors ${
                viewMode === 'source'
                  ? 'bg-primary text-white'
                  : 'text-white/60 hover:text-white hover:bg-white/10'
              }`}
            >
              Source
            </button>
            <button
              type="button"
              onClick={() => setViewMode('preview')}
              disabled={disabled}
              className={`px-2 py-1 text-xs rounded transition-colors ${
                viewMode === 'preview'
                  ? 'bg-primary text-white'
                  : 'text-white/60 hover:text-white hover:bg-white/10'
              }`}
            >
              Preview
            </button>
          </div>

          {/* Formatting Buttons (only in visual mode) */}
          {viewMode === 'visual' &&
            toolbarButtons.map((button, index) => (
              <button
                key={index}
                type="button"
                onClick={button.action}
                disabled={disabled}
                title={button.label}
                className={`p-1.5 rounded transition-colors ${
                  button.active
                    ? 'bg-primary/30 text-primary'
                    : 'text-white/60 hover:text-white hover:bg-white/10'
                } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {button.icon}
              </button>
            ))}
        </div>

        {/* Editor Content Area */}
        <div style={{ minHeight }} className="relative">
          {/* Visual Editor */}
          {viewMode === 'visual' && (
            <div
              ref={editorRef}
              contentEditable={!disabled}
              onInput={handleContentChange}
              onKeyDown={handleKeyDown}
              onSelect={handleSelect}
              onFocus={updateToolbarState}
              dangerouslySetInnerHTML={{ __html: value || '' }}
              className={`p-4 min-h-full focus:outline-none prose prose-invert prose-sm max-w-none ${
                disabled ? 'opacity-50 cursor-not-allowed' : ''
              }`}
              style={{ minHeight }}
              data-placeholder={placeholder}
            />
          )}

          {/* Source Code Editor */}
          {viewMode === 'source' && (
            <textarea
              value={value}
              onChange={handleSourceChange}
              disabled={disabled}
              placeholder={placeholder}
              className="w-full p-4 bg-transparent text-white/80 font-mono text-sm resize-none focus:outline-none"
              style={{ minHeight }}
            />
          )}

          {/* Preview Mode */}
          {viewMode === 'preview' && (
            <div
              className="p-4 prose prose-invert prose-sm max-w-none"
              style={{ minHeight }}
              dangerouslySetInnerHTML={{ __html: value || '<p class="text-white/40 italic">No content to preview</p>' }}
            />
          )}

          {/* Placeholder (for visual mode) */}
          {viewMode === 'visual' && !value && (
            <div className="absolute top-4 left-4 text-white/40 pointer-events-none">
              {placeholder}
            </div>
          )}
        </div>
      </div>

      {/* Error Message */}
      {error && <p className="text-red-400 text-sm">{error}</p>}
      {uploadError && <p className="text-red-400 text-sm">Image upload: {uploadError}</p>}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        onChange={handleImageSelected}
        className="hidden"
        aria-hidden="true"
        tabIndex={-1}
      />

      {/* Character Count */}
      <div className="text-right text-xs text-white/40">
        {value.length.toLocaleString()} characters
      </div>
    </div>
  );
}

export default RichTextEditor;
